import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { useTwoFactor } from "@/components/TwoFactorProvider";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  User,
  Monitor,
  Bell,
  ShieldCheck,
  Gavel,
  Globe,
  Save,
} from "lucide-react";

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [dirty, setDirty] = useState({}); // partial patch
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const twoFactor = useTwoFactor();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      setSettings(data);
      setDirty({});
      setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const patchField = (section, key, value) => {
    setDirty((prev) => ({
      ...prev,
      [section]: { ...(prev[section] || {}), [key]: value },
    }));
  };

  const merged = (section, key) => {
    return dirty?.[section]?.[key] ?? settings?.[section]?.[key];
  };

  const hasChanges = Object.keys(dirty).length > 0;

  const save = async () => {
    if (!hasChanges) return;
    setSaving(true);
    try {
      await twoFactor.requireCode({
        actionLabel: `Aggiorna impostazioni (${Object.keys(dirty).join(", ")})`,
        run: async (code) => {
          await api.patch("/settings", dirty, { headers: { "X-Second-Factor": code } });
          toast.success("Impostazioni salvate");
          await load();
        },
      });
    } catch (e) {
      if (e?.message !== "cancelled") {
        setErr(e?.response?.data?.detail || e.message || "Errore");
      }
    } finally {
      setSaving(false);
    }
  };

  if (err) return <div className="border border-red-900/60 bg-red-950/20 px-4 py-3 font-mono text-xs text-red-300">{err}</div>;
  if (!settings) return <div className="font-mono text-xs text-neutral-500"><span className="cursor-blink">LOADING</span></div>;

  return (
    <div data-testid={TID.settingsPage} className="space-y-6 max-w-4xl">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <p className="text-sm text-neutral-400 max-w-2xl">
          Configurazione della piattaforma. Le modifiche richiedono il secondo fattore
          (000000 in scaffold).
        </p>
        <Button
          data-testid={TID.settingsSaveBtn}
          onClick={save}
          disabled={!hasChanges || saving}
          className="rounded-none bg-[#EDEDED] text-black hover:bg-white disabled:opacity-40 font-mono text-xs uppercase tracking-widest"
        >
          <Save className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
          {saving ? "Salvataggio…" : hasChanges ? `Salva (${Object.keys(dirty).length})` : "Nessuna modifica"}
        </Button>
      </div>

      {/* Profilo */}
      <Section testId={TID.settingsProfile} title="Profilo" icon={User}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Nome visualizzato">
            <Input
              value={merged("profile", "display_name") || ""}
              onChange={(e) => patchField("profile", "display_name", e.target.value)}
              className="rounded-none border-[#1F1F1F] bg-[#050505] focus-visible:ring-0 focus-visible:border-[#EDEDED]"
            />
          </Field>
          <Field label="Ruolo">
            <Input
              value={merged("profile", "role") || ""}
              disabled
              className="rounded-none border-[#1F1F1F] bg-[#050505] font-mono opacity-70"
            />
            <Hint>fissato a owner in scaffold</Hint>
          </Field>
          <Field label="Iniziali avatar">
            <Input
              value={merged("profile", "avatar_initials") || ""}
              onChange={(e) => patchField("profile", "avatar_initials", e.target.value.slice(0, 3).toUpperCase())}
              maxLength={3}
              className="rounded-none border-[#1F1F1F] bg-[#050505] font-mono uppercase focus-visible:ring-0 focus-visible:border-[#EDEDED]"
            />
          </Field>
        </div>
      </Section>

      {/* UI */}
      <Section testId={TID.settingsUi} title="Preferenze UI" icon={Monitor}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Densità">
            <div className="grid grid-cols-2 gap-1.5">
              {["comfortable", "compact"].map((v) => (
                <ChoicePill
                  key={v}
                  active={merged("ui", "density") === v}
                  onClick={() => patchField("ui", "density", v)}
                >
                  {v}
                </ChoicePill>
              ))}
            </div>
          </Field>
          <Field label="Landing dopo login">
            <div className="grid grid-cols-2 gap-1.5">
              {["dashboard", "leads"].map((v) => (
                <ChoicePill
                  key={v}
                  active={merged("ui", "default_landing") === v}
                  onClick={() => patchField("ui", "default_landing", v)}
                >
                  {v}
                </ChoicePill>
              ))}
            </div>
          </Field>
          <Field label="Sidebar collassata">
            <SwitchRow
              checked={Boolean(merged("ui", "sidebar_collapsed"))}
              onChange={(v) => patchField("ui", "sidebar_collapsed", v)}
              label="Nascondi label"
            />
          </Field>
        </div>
      </Section>

      {/* Notifiche */}
      <Section testId={TID.settingsNotifications} title="Notifiche" icon={Bell}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            ["loop_opened", "Loop aperti"],
            ["decision_approved", "Decisioni approvate"],
            ["quote_generated", "Preventivo generato"],
            ["prd_generated", "PRD generato"],
            ["agent_degraded", "Agente in DEGRADED"],
          ].map(([k, label]) => (
            <div key={k} className="border border-[#1F1F1F] bg-[#050505] px-4 py-3">
              <SwitchRow
                label={label}
                checked={Boolean(merged("notifications", k))}
                onChange={(v) => patchField("notifications", k, v)}
              />
            </div>
          ))}
        </div>
      </Section>

      {/* Governance */}
      <Section testId={TID.settingsGovernance} title="Governance" icon={Gavel}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Chiudi loop dopo (giorni)">
            <Input
              type="number"
              min="1"
              max="365"
              value={merged("governance", "auto_close_loops_days") ?? ""}
              onChange={(e) => patchField("governance", "auto_close_loops_days", Number(e.target.value))}
              className="rounded-none border-[#1F1F1F] bg-[#050505] font-mono focus-visible:ring-0 focus-visible:border-[#EDEDED]"
            />
          </Field>
          <Field label="Richiedi motivazione (rationale)">
            <SwitchRow
              checked={Boolean(merged("governance", "require_rationale"))}
              onChange={(v) => patchField("governance", "require_rationale", v)}
              label="obbligatoria su ogni DEC"
            />
          </Field>
        </div>
      </Section>

      {/* Security & Dominio (read-only) */}
      <Section testId={TID.settingsSecurity} title="Sicurezza & Accesso" icon={ShieldCheck}>
        <div className="space-y-3 font-mono text-xs">
          <ReadRow label="Email autorizzate">
            {settings.authorized_emails.map((e) => (
              <span key={e} className="inline-block mr-2 bg-[#050505] border border-[#1F1F1F] px-2 py-0.5">
                {e}
              </span>
            ))}
          </ReadRow>
          <ReadRow label="Second factor code (hint)">
            <span className="text-[#EDEDED] tracking-[0.4em]">{settings.second_factor_code_hint}</span>
          </ReadRow>
          <ReadRow label="Session email">
            <span className="text-[#EDEDED]">{settings.session_email}</span>
          </ReadRow>
        </div>
      </Section>

      <Section title="Dominio" icon={Globe}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 font-mono text-xs">
          <ReadRow label="Root">
            <span className="text-[#EDEDED]">{settings.domain?.root}</span>
          </ReadRow>
          <ReadRow label="Subdomain (questa app)">
            <span className="text-[#EDEDED]">{settings.domain?.subdomain}</span>
          </ReadRow>
        </div>
        <div className="font-mono text-[10px] text-neutral-500 mt-3">
          Riferimento decisionale · DEC-B (Governance)
        </div>
      </Section>
    </div>
  );
}

function Section({ title, icon: Icon, children, testId }) {
  return (
    <section data-testid={testId} className="border border-[#1F1F1F] bg-[#0C0C0C]">
      <header className="border-b border-[#1F1F1F] px-4 py-3 flex items-center gap-2 bg-[#141414]">
        <Icon className="w-3.5 h-3.5 text-neutral-500" strokeWidth={1.5} />
        <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-400">
          {title}
        </span>
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-500">
        {label}
      </label>
      {children}
    </div>
  );
}

function Hint({ children }) {
  return <div className="font-mono text-[10px] text-neutral-500">{children}</div>;
}

function ReadRow({ label, children }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-[#1F1F1F] first:border-t-0 pt-2 first:pt-0">
      <div className="text-neutral-500 uppercase tracking-[0.24em] text-[10px] w-48 shrink-0">{label}</div>
      <div className="flex-1">{children}</div>
    </div>
  );
}

function ChoicePill({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={[
        "border font-mono text-[10px] uppercase tracking-widest px-2 py-2 transition-colors text-center",
        active
          ? "border-[#EDEDED] bg-[#EDEDED] text-black"
          : "border-[#1F1F1F] text-neutral-400 hover:text-[#EDEDED] hover:border-[#333]",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

function SwitchRow({ label, checked, onChange }) {
  return (
    <label className="flex items-center justify-between gap-4 cursor-pointer">
      <span className="text-sm text-neutral-300">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} />
    </label>
  );
}
